import { useLocalization } from '@components/Localization'
import LocalizedLink from '@components/Localization/LocalizedLink'
import { Heading, Image, linkStyle, Text } from '@gassan-ui'
import { getKontentImage } from '@lib/kontent'
import { format, fromUnixTime, roundToNearestMinutes } from 'date-fns'
import React, { FC } from 'react'
import styled from 'styled-components'
import { motion, AnimatePresence } from 'framer-motion'

const Card = styled(motion.a)`
  img {
    transition: opacity 0.2s ease-out;
  }
  &:hover,
  &:focus {
    img {
      opacity: 0.7;
    }
  }
`

Card.defaultProps = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { ease: 'easeOut', duration: 0.3 }
}

const Link = styled.span`
  ${linkStyle};
  line-height: 1.45;
  border-bottom: 1px solid currentColor;
  color: ${p => p.theme.colors.tabaccoBrown};
  padding-bottom: 0.25rem;
`

type Props = {
  urlSlug: string
  image?: string
  title: string
  address: string
  country: string
  openendTodayTill: number | null
}

const StoreCard: FC<Props> = ({ urlSlug, image, title, address, country, openendTodayTill }) => {
  const { messages, lang } = useLocalization()
  let opened = ''
  const timezone = country === 'singapore' ? '(SGT)' : '(CEST)'
  if (openendTodayTill) {
    opened =
      lang === 'nl'
        ? `Vandaag geopend tot ${format(
            roundToNearestMinutes(fromUnixTime(openendTodayTill)),
            'HH:mm'
          )} uur ${timezone}`
        : `Opened today till ${format(
            roundToNearestMinutes(fromUnixTime(openendTodayTill)),
            'HH:mm'
          )} ${timezone}`
  } else {
    opened = lang === 'nl' ? 'Vandaag gesloten' : 'Closed today'
  }
  return (
    <AnimatePresence>
      <LocalizedLink href="/[...slug]" as={`/${messages.urlSlugItems.stores}/${urlSlug}`}>
        <Card>
          {image && (
            <Image
              ratio={0.55}
              placeholderSrc={getKontentImage(image, { width: 50 })}
              sources={[
                getKontentImage(image, { width: 400 }),
                getKontentImage(image, { width: 400 }),
                getKontentImage(image, { width: 600 })
              ]}
            />
          )}

          <Heading
            as="div"
            variant="h4"
            mt={{ _: '1rem', medium: '2rem' }}
            mb={{ _: '1rem', medium: '1rem', large: '1.5rem' }}
          >
            {title}
          </Heading>
          <Text variant="regular" as="div" mb=".5rem">
            {address}
          </Text>
          <Text variant="regular" as="div" color="shades.500" mb={{ _: '1rem', medium: '1.5rem' }}>
            {opened}
          </Text>
          <Link>
            {lang === 'nl' ? 'Informatie en openingstijden' : 'Information and opening hours'}
          </Link>
        </Card>
      </LocalizedLink>
    </AnimatePresence>
  )
}

export default StoreCard
